import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { Logo } from '@/components/Logo';

export default function SmsTermsPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="sms-terms-page">
      <header className="border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="sms-terms-home-link"><Logo size="sm" /></Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
            data-testid="sms-terms-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-sm bg-[#0047AB]/10 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-[#0047AB]" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
            SMS Terms &amp; Conditions
          </h1>
        </div>
        <p className="text-sm text-slate-500 mb-10">
          A2P 10DLC Program Disclosure &middot; Last updated:{' '}
          {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        <div className="prose prose-slate max-w-none text-sm leading-relaxed space-y-6">
          <section data-testid="sms-section-program">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Program Name &amp; Description</h2>
            <p className="text-slate-700">
              Real Leads CRM (operated by <strong>Encore Business Services Group LLC</strong>) offers a
              transactional and conversational SMS program to help our customers (sales agents, brokers,
              and CRM users) communicate with their own leads, receive account notifications, appointment
              reminders, lead-share alerts, two-factor authentication codes, and platform updates. Messages
              are sent on an as-needed basis triggered by user activity within the CRM.
            </p>
          </section>

          <section data-testid="sms-section-optin">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">How to Opt In</h2>
            <p className="text-slate-700">
              By providing your mobile phone number during account registration, in your account settings,
              or by entering it into any opt-in form on this website (including the registration page at{' '}
              <Link to="/register" className="text-[#0047AB] underline">/register</Link>), and submitting
              the form, you expressly consent to receive SMS text messages from Real Leads CRM at the number
              provided. Consent is not a condition of purchase. You may opt out at any time.
            </p>
          </section>

          <section data-testid="sms-section-frequency">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Message Frequency</h2>
            <p className="text-slate-700">
              Message frequency varies based on your account activity. You may receive up to{' '}
              <strong>10 messages per week</strong> for transactional notifications and reminders.
            </p>
          </section>

          <section data-testid="sms-section-cost">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Message &amp; Data Rates</h2>
            <p className="text-slate-700">
              <strong>Message and data rates may apply.</strong> Real Leads CRM does not charge you to
              receive SMS messages, but your mobile carrier&apos;s standard messaging and data rates may
              apply to messages sent to and from your device. Please check with your mobile carrier for
              details about your messaging plan.
            </p>
          </section>

          <section data-testid="sms-section-optout">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">How to Opt Out (STOP)</h2>
            <p className="text-slate-700">
              You can cancel the SMS service at any time. Simply text <strong>STOP</strong> to the
              short code or long code that sent you a message. After you send the SMS message{' '}
              <strong>&quot;STOP&quot;</strong> to us, we will send you a confirmation SMS to verify that
              you have been unsubscribed. After this, you will no longer receive SMS messages from us. If
              you want to join again, sign up as you did the first time and we will start sending SMS
              messages to you again.
            </p>
          </section>

          <section data-testid="sms-section-help">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Getting Help (HELP)</h2>
            <p className="text-slate-700">
              If at any time you forget what keywords are supported, text <strong>HELP</strong> to the
              number that sent you a message. We will respond with instructions on how to use our service
              as well as how to unsubscribe. For additional help, email{' '}
              <a href="mailto:support@realleadscrm.com" className="text-[#0047AB] underline">
                support@realleadscrm.com
              </a>
              .
            </p>
          </section>

          <section data-testid="sms-section-carriers">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Supported Carriers</h2>
            <p className="text-slate-700">
              Our SMS program is supported on all major U.S. carriers, including AT&amp;T, T-Mobile,
              Verizon Wireless, Sprint, Boost Mobile, U.S. Cellular, MetroPCS, Cricket Wireless, Virgin
              Mobile, and others. Carriers are not liable for delayed or undelivered messages.
            </p>
          </section>

          <section data-testid="sms-section-privacy">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Privacy &amp; Data Sharing</h2>
            <p className="text-slate-700">
              <strong>
                No mobile information will be shared with third parties or affiliates for marketing or
                promotional purposes.
              </strong>{' '}
              Information sharing to subcontractors that support our SMS service (such as our SMS gateway
              provider, Twilio) is permitted strictly to deliver messages. All other use categories
              exclude text messaging originator opt-in data and consent; this information will not be
              shared with any third parties. For full details, see our{' '}
              <Link to="/privacy" className="text-[#0047AB] underline" data-testid="sms-privacy-link">
                Privacy Policy
              </Link>
              .
            </p>
          </section>

          <section data-testid="sms-section-contact">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Contact Us</h2>
            <p className="text-slate-700">
              Encore Business Services Group LLC
              <br />
              Email:{' '}
              <a href="mailto:support@realleadscrm.com" className="text-[#0047AB] underline">
                support@realleadscrm.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 text-xs text-slate-400">
          By using Real Leads CRM and providing your mobile number, you acknowledge that you have read,
          understood, and agree to these SMS Terms &amp; Conditions.
        </div>
      </main>
    </div>
  );
}
